import type { OverlapEntrySource } from "../types";

/**
 * 「待消费的入口上下文」（EDG-561 fe-spec §2.3 / §3.5）：`overlap_result_view` 需要知道
 * **这次结果是从哪个入口来的、输入来自哪里**，但结果出现在 Chat 的消息流里，离入口很远。
 *
 * 入口处写一半、完成处写另一半，结果曝光时**单次消费**（之后为 `null`，防止后续结果沿用旧入口）：
 * - 入口（Brief 卡 / Invest / 推荐问题 / 广告落点）→ `entry_source`；
 * - 持仓已存在 / Plaid 成功 / 上传成功 → `input_source`。
 *
 * 模块级 + sessionStorage 兜底（刷新不丢），不新增 Valtio store（AGENTS.md Ask first）。
 */
export type OverlapInputSource = "upload" | "plaid" | "existing";

export interface PendingOverlapContext {
  entry_source: OverlapEntrySource;
  input_source: OverlapInputSource;
}

const STORAGE_KEY = "etf_overlap_pending_ctx";

type Stored = Partial<PendingOverlapContext>;

let memory: Stored | null = null;

const readStorage = (): Stored | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
};

const writeStorage = (value: Stored | null): void => {
  try {
    if (value) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // 隐私模式下写不进：退化为仅内存，刷新会丢，不影响功能
  }
};

const current = (): Stored | null => memory ?? readStorage();

/** 合并写入：入口只带 `entry_source`，完成处只带 `input_source`。 */
export const setPendingOverlapContext = (patch: Stored): void => {
  const next: Stored = { ...(current() ?? {}), ...patch };
  memory = next;
  writeStorage(next);
};

/** 读取但不消费（广告链路返回判断用）。 */
export const peekPendingOverlapContext = (): Stored | null => current();

/** 取走一次；之后为 `null`。 */
export const consumePendingOverlapContext = (): Stored | null => {
  const value = current();
  memory = null;
  writeStorage(null);
  return value;
};
